import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  MessageSquare,
  Send,
  Search,
  Plus,
  Users,
  FolderOpen,
  Building2,
  Loader2,
  Check,
  CheckCheck,
  Eye,
  Clock,
  ArrowLeft,
  Paperclip,
  Mic,
  MicOff,
  AlertTriangle,
  UserPlus,
  Image as ImageIcon,
  FileText,
  File,
  X,
  Download,
  CheckCircle2,
  ListTodo,
  Package,
  Upload,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  ClipboardCheck,
  Globe,
  Video
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

// =============================================
// UNIFIED CHAT PAGE
// Combines: Internal Chat + Project Chat + Direct Client Chat
// With read indicators for agency/team only
// =============================================

const UnifiedChatPage = () => {
  const { user, isClient } = useAuth();
  const { theme } = useTheme();
  // For clients: default to 'projects' tab. For agency: default to 'internal'
  const [activeTab, setActiveTab] = useState(isClient ? 'projects' : 'internal');
  const [loading, setLoading] = useState(true);
  
  // Internal Chat State (agency only)
  const [internalConversations, setInternalConversations] = useState([]);
  const [activeInternalConv, setActiveInternalConv] = useState(null);
  const [internalMessages, setInternalMessages] = useState([]);
  
  // Project Chat State
  const [projectConversations, setProjectConversations] = useState([]);
  const [activeProjectConv, setActiveProjectConv] = useState(null);
  const [projectMessages, setProjectMessages] = useState([]);
  
  // Direct Client Chat State
  const [clientConversations, setClientConversations] = useState([]);
  const [activeClientConv, setActiveClientConv] = useState(null);
  const [clientMessages, setClientMessages] = useState([]);
  
  // Common State
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [mobileFullscreen, setMobileFullscreen] = useState(false); // True fullscreen mode for WhatsApp-like experience
  const [selectedNewConvRecipient, setSelectedNewConvRecipient] = useState('');
  
  // Detect if mobile
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // File attachment state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // Messages scroll refs - to auto-scroll to latest message
  const messagesEndRef = React.useRef(null);
  
  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);
  
  // Chat type colors - PASTEL VERSION
  const chatColors = {
    internal: { bg: 'bg-sky-300', text: 'text-sky-700', border: 'border-sky-200', light: 'bg-sky-50', accent: '#7dd3fc' },
    project: { bg: 'bg-slate-400', text: 'text-slate-700', border: 'border-slate-300', light: 'bg-slate-100', accent: '#94a3b8' },
    client: { bg: 'bg-teal-300', text: 'text-teal-700', border: 'border-teal-200', light: 'bg-teal-50', accent: '#5eead4' }
  };
  
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [showAudioOptions, setShowAudioOptions] = useState(false);
  const mediaRecorderRef = React.useRef(null);
  const audioChunksRef = React.useRef([]);
  
  // Task completion state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [projectTasks, setProjectTasks] = useState([]);
  const [completingTask, setCompletingTask] = useState(false);
  
  // Deliverable/Version state
  const [showDeliverableModal, setShowDeliverableModal] = useState(false);
  const [deliverableFile, setDeliverableFile] = useState(null);
  const [deliverableComment, setDeliverableComment] = useState('');
  const [deliverableTitle, setDeliverableTitle] = useState('');
  const [deliverableType, setDeliverableType] = useState('file'); // 'file', 'link_web', 'link_video', 'image', 'pdf'
  const [deliverableLink, setDeliverableLink] = useState('');
  const [sendingDeliverable, setSendingDeliverable] = useState(false);
  const [selectedTaskForDeliverable, setSelectedTaskForDeliverable] = useState('');
  const [isNewVersion, setIsNewVersion] = useState(false);
  const [previousDeliverableId, setPreviousDeliverableId] = useState('');
  const [projectDeliverables, setProjectDeliverables] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackDeliverableId, setFeedbackDeliverableId] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const deliverableInputRef = React.useRef(null);
  
  // Image preview modal state
  const [previewImage, setPreviewImage] = useState(null);
  
  // Unread counts
  const [unreadCounts, setUnreadCounts] = useState({
    internal: 0,
    projects: 0,
    clients: 0
  });

  useEffect(() => {
    fetchAllData();
    // Polling for new messages
    const interval = setInterval(fetchAllData, 15000);
    return () => clearInterval(interval);
  }, []);

  // Reset mobile state when changing tabs
  useEffect(() => {
    setMobileShowChat(false);
    // Reset active conversations when switching tabs
    if (activeTab === 'internal') {
      setActiveProjectConv(null);
      setActiveClientConv(null);
    } else if (activeTab === 'projects') {
      setActiveInternalConv(null);
      setActiveClientConv(null);
    } else if (activeTab === 'clients') {
      setActiveInternalConv(null);
      setActiveProjectConv(null);
    }
  }, [activeTab]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchInternalConversations(),
        fetchProjectConversations(),
        fetchClientConversations(),
        fetchTeamMembers(),
        fetchClients()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInternalConversations = async () => {
    try {
      const res = await api.get('/internal-chat/conversations');
      const convs = res.data || [];
      setInternalConversations(convs);
      
      // Calculate unread
      const unread = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnreadCounts(prev => ({ ...prev, internal: unread }));
    } catch (error) {
      console.error('Error fetching internal conversations:', error);
    }
  };

  const fetchProjectConversations = async () => {
    try {
      const res = await api.get('/projects/chat/centralized');
      // API returns { projects: [...], messages: [...], team_members: [...] }
      const convs = res.data?.projects || [];
      setProjectConversations(convs);
      
      // Calculate unread
      const unread = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnreadCounts(prev => ({ ...prev, projects: unread }));
    } catch (error) {
      console.error('Error fetching project conversations:', error);
    }
  };

  const fetchClientConversations = async () => {
    try {
      // Direct client conversations via new endpoint
      const res = await api.get('/client-chat/conversations');
      const convs = res.data || [];
      setClientConversations(convs);
      
      const unread = convs.reduce((sum, c) => sum + (c.unread_count || 0), 0);
      setUnreadCounts(prev => ({ ...prev, clients: unread }));
    } catch (error) {
      // May not exist yet - that's ok
    }
  };

  const fetchTeamMembers = async () => {
    try {
      // Get both team_members and project_managers for internal chat
      const [teamRes, pmRes, agencyRes] = await Promise.all([
        api.get('/users/', { params: { role: 'team_member' } }),
        api.get('/users/', { params: { role: 'project_manager' } }),
        api.get('/users/', { params: { role: 'agency' } })
      ]);
      
      const allMembers = [
        ...(teamRes.data || []),
        ...(pmRes.data || []),
        ...(agencyRes.data || [])
      ];
      
      // Remove duplicates by id
      const uniqueMembers = allMembers.filter((m, idx, arr) => 
        arr.findIndex(x => x.id === m.id) === idx
      );
      
      setTeamMembers(uniqueMembers);
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get('/users/', { params: { role: 'client' } });
      setClients(res.data || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchInternalMessages = async (conversationId) => {
    try {
      const res = await api.get(`/internal-chat/conversations/${conversationId}/messages`);
      setInternalMessages(res.data || []);
      
      // Mark as read
      await api.post(`/internal-chat/conversations/${conversationId}/read`);
      fetchInternalConversations();
      
      // Scroll to bottom after messages loaded - with delay for DOM render
      setTimeout(() => scrollToBottom(), 200);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchProjectMessages = async (projectId) => {
    try {
      const res = await api.get(`/projects/${projectId}/messages`);
      setProjectMessages(res.data || []);
      
      // Mark as read
      await api.post(`/projects/${projectId}/messages/mark-read`);
      fetchProjectConversations();
      
      // Scroll to bottom after messages loaded - with delay for DOM render
      setTimeout(() => scrollToBottom(), 200);
    } catch (error) {
      console.error('Error fetching project messages:', error);
    }
  };

  const fetchClientMessages = async (conversationId) => {
    try {
      const res = await api.get(`/client-chat/conversations/${conversationId}/messages`);
      setClientMessages(res.data || []);
      
      // Mark as read
      await api.post(`/client-chat/conversations/${conversationId}/read`);
      fetchClientConversations();
      
      // Scroll to bottom after messages loaded - with delay for DOM render
      setTimeout(() => scrollToBottom(), 200);
    } catch (error) {
      console.error('Error fetching client messages:', error);
    }
  };

  const handleSelectInternalConv = (conv) => {
    setActiveInternalConv(conv);
    fetchInternalMessages(conv.id);
    setMobileShowChat(true);
    if (isMobile) setMobileFullscreen(true);
  };

  const handleSelectProjectConv = (conv) => {
    setActiveProjectConv(conv);
    fetchProjectMessages(conv.id);  // conv.id is the project_id from centralized endpoint
    fetchProjectDeliverables(conv.id);  // Also fetch deliverables
    setMobileShowChat(true);
    if (isMobile) setMobileFullscreen(true);
  };

  const handleSelectClientConv = (conv) => {
    setActiveClientConv(conv);
    fetchClientMessages(conv.id);
    setMobileShowChat(true);
    if (isMobile) setMobileFullscreen(true);
  };

  // Start new conversation with a client
  const handleStartClientConversation = async (client) => {
    try {
      const res = await api.post('/client-chat/conversations', {
        client_id: client.id,
        subject: 'Chat direct'
      });
      
      const conv = res.data;
      setClientConversations(prev => [conv, ...prev.filter(c => c.id !== conv.id)]);
      handleSelectClientConv(conv);
      toast.success('Conversation créée');
    } catch (error) {
      toast.error('Erreur lors de la création de la conversation');
    }
  };

  const handleSendInternalMessage = async () => {
    if (!activeInternalConv) {
      toast.error('Veuillez sélectionner une conversation');
      return;
    }
    if (!newMessage.trim() && !selectedFile && !audioBlob) return;
    
    setSending(true);
    try {
      let fileUrl = null;
      let fileName = null;
      let messageType = 'text';
      
      // Handle file upload (including audio)
      if (selectedFile || audioBlob) {
        const fileToUpload = selectedFile || new File([audioBlob], 'audio-message.webm', { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', fileToUpload);
        
        const uploadRes = await api.post(`/internal-chat/conversations/${activeInternalConv.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        fileUrl = uploadRes.data.file_url;
        fileName = uploadRes.data.file_name;
        messageType = uploadRes.data.message_type;
      }
      
      // Send message with file info
      await api.post(`/internal-chat/conversations/${activeInternalConv.id}/messages`, {
        content: newMessage || (audioBlob ? '🎤 Message vocal' : fileName || ''),
        message_type: fileUrl ? messageType : 'text',
        file_url: fileUrl,
        file_name: fileName
      });
      
      setNewMessage('');
      setSelectedFile(null);
      setAudioBlob(null);
      setShowAudioOptions(false);
      fetchInternalMessages(activeInternalConv.id);
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Send internal message error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleSendProjectMessage = async () => {
    if (!activeProjectConv) {
      toast.error('Veuillez sélectionner un projet');
      return;
    }
    if (!newMessage.trim() && !selectedFile && !audioBlob) return;
    
    setSending(true);
    try {
      if (selectedFile || audioBlob) {
        // Send with file attachment (including audio)
        const formData = new FormData();
        const fileToUpload = selectedFile || new File([audioBlob], 'audio-message.webm', { type: 'audio/webm' });
        formData.append('file', fileToUpload);
        formData.append('message', newMessage || (audioBlob ? '🎤 Message vocal' : selectedFile.name));
        
        await api.post(`/projects/${activeProjectConv.id}/chat/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setSelectedFile(null);
        setAudioBlob(null);
        setShowAudioOptions(false);
      } else {
        // Send text only
        await api.post(`/projects/${activeProjectConv.id}/messages`, {
          content: newMessage,
          message_type: 'text'
        });
      }
      setNewMessage('');
      fetchProjectMessages(activeProjectConv.id);
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Send project message error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleSendClientMessage = async () => {
    if (!activeClientConv) {
      toast.error('Veuillez sélectionner une conversation');
      return;
    }
    if (!newMessage.trim() && !selectedFile && !audioBlob) return;
    
    setSending(true);
    try {
      let fileUrl = null;
      let fileName = null;
      let messageType = 'text';
      
      // Handle file upload (including audio)
      if (selectedFile || audioBlob) {
        const fileToUpload = selectedFile || new File([audioBlob], 'audio-message.webm', { type: 'audio/webm' });
        const formData = new FormData();
        formData.append('file', fileToUpload);
        
        const uploadRes = await api.post(`/client-chat/conversations/${activeClientConv.id}/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        fileUrl = uploadRes.data.file_url;
        fileName = uploadRes.data.file_name;
        messageType = uploadRes.data.message_type;
      }
      
      // Send message with file info
      const formData = new FormData();
      formData.append('content', newMessage || (audioBlob ? '🎤 Message vocal' : fileName || ''));
      formData.append('message_type', fileUrl ? messageType : 'text');
      if (fileUrl) {
        formData.append('file_url', fileUrl);
        formData.append('file_name', fileName);
      }
      
      await api.post(`/client-chat/conversations/${activeClientConv.id}/messages`, formData);
      setNewMessage('');
      setSelectedFile(null);
      setAudioBlob(null);
      setShowAudioOptions(false);
      fetchClientMessages(activeClientConv.id);
      // Scroll to bottom after sending
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Send client message error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  // Read indicator component - only visible for agency/team
  const ReadIndicator = ({ message, isOwnMessage }) => {
    if (isClient) return null; // Don't show to clients
    if (!isOwnMessage) return null; // Only show for own messages
    
    const isRead = message.read_by && message.read_by.length > 0;
    
    return (
      <span className="ml-1" title={isRead ? 'Lu' : 'Envoyé'}>
        {isRead ? (
          <CheckCheck className="w-3 h-3 text-sky-500 inline" />
        ) : (
          <Check className="w-3 h-3 text-slate-400 inline" />
        )}
      </span>
    );
  };

  // Conversation list item component
  const ConversationItem = ({ conv, isActive, onClick, type }) => {
    const hasUnread = (conv.unread_count || 0) > 0;
    
    return (
      <div
        onClick={onClick}
        className={cn(
          "flex items-center gap-3 p-3 cursor-pointer rounded-lg transition-all",
          isActive ? "bg-slate-100" : "hover:bg-slate-50",
          hasUnread && "border-l-4 border-sky-400"
        )}
      >
        <div className="relative">
          <Avatar 
            src={conv.avatar_url || conv.other_user?.avatar_url || conv.client_avatar} 
            name={conv.name || conv.other_user?.name || 'Chat'} 
            size="md"
          />
          {conv.is_online && (
            <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={cn("font-medium truncate", hasUnread && "text-slate-900")}>
              {conv.name || conv.other_user?.name || 'Sans nom'}
            </span>
            <div className="flex items-center gap-1 shrink-0">
              {type === 'project' && conv.urgency_label && (
                <Badge variant={
                  conv.urgency_label === 'En retard' ? 'destructive' :
                  conv.urgency_label === 'Urgent' ? 'destructive' : 'secondary'
                } className="text-[10px] px-1.5 py-0">
                  {conv.urgency_label}
                </Badge>
              )}
              {hasUnread && (
                <Badge className="bg-sky-400 text-white text-xs px-1.5 py-0.5 min-w-[20px] text-center">
                  {conv.unread_count}
                </Badge>
              )}
            </div>
          </div>
          
          {/* Project-specific: client name and deadline */}
          {type === 'project' && (
            <div className="flex items-center gap-2 text-[10px] text-slate-500 mt-0.5">
              {conv.client_name && (
                <span className="flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {conv.client_name}
                </span>
              )}
              {conv.deadline && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(conv.deadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                </span>
              )}
            </div>
          )}
          
          <p className={cn(
            "text-xs truncate mt-0.5",
            hasUnread ? "text-slate-700 font-medium" : "text-slate-500"
          )}>
            {conv.last_message?.content || conv.last_message || conv.last_message_preview || 'Aucun message'}
          </p>
          
          {/* Team members avatars for projects */}
          {type === 'project' && conv.team_members && conv.team_members.length > 0 && (
            <div className="flex items-center gap-1 mt-1">
              {conv.team_members.slice(0, 4).map((member, idx) => (
                <div key={member.id || idx} className="relative" title={member.name}>
                  <Avatar 
                    src={member.avatar_url} 
                    name={member.name} 
                    size="xs"
                    className={cn(
                      "w-5 h-5 border border-white",
                      !member.is_available && "opacity-50"
                    )}
                  />
                  {member.is_available && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-500 rounded-full border border-white" />
                  )}
                </div>
              ))}
              {conv.team_members.length > 4 && (
                <span className="text-[10px] text-slate-400">+{conv.team_members.length - 4}</span>
              )}
            </div>
          )}
          
          {conv.last_message_time || conv.last_message_at ? (
            <p className="text-[10px] text-slate-400 mt-0.5">
              {new Date(conv.last_message_time || conv.last_message_at).toLocaleString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit',
                day: 'numeric',
                month: 'short'
              })}
            </p>
          ) : null}
        </div>
      </div>
    );
  };

  // Message component with read indicator and file support
  const MessageBubble = ({ message, isOwn, chatType = 'internal' }) => {
    const colors = chatColors[chatType] || chatColors.internal;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Determine if message has an attachment
    const hasAttachment = message.message_type === 'image' || message.message_type === 'file' || message.message_type === 'voice' || message.attachment_url || message.file_url;
    let attachmentUrl = message.attachment_url || message.file_url;
    let fileName = message.file_name || (attachmentUrl ? attachmentUrl.split('/').pop() : '');
    
    // Check if this is a deliverable message
    const isDeliverable = message.message_type === 'deliverable';
    const isDeliverableApproved = message.message_type === 'deliverable_approved';
    const isDeliverableRevision = message.message_type === 'deliverable_revision';
    
    // Build full URL for attachment
    const getFullUrl = (url) => {
      if (!url) return null;
      if (url.startsWith('http')) return url;
      // Si l'URL commence par /uploads ou /static, ajouter le préfixe /api
      if (url.startsWith('/uploads') || url.startsWith('/static')) {
        return `${BACKEND_URL}/api${url}`;
      }
      return `${BACKEND_URL}${url}`;
    };
    
    // Get deliverable status from projectDeliverables
    const getDeliverableStatus = () => {
      if (!message.deliverable_id) return null;
      return projectDeliverables.find(d => d.id === message.deliverable_id);
    };
    
    const deliverable = getDeliverableStatus();
    
    // For deliverables, use the deliverable's file_url if available
    const deliverableFileUrl = deliverable?.file_url || deliverable?.attachment_url;
    const deliverableFileName = deliverable?.file_name || (deliverableFileUrl ? deliverableFileUrl.split('/').pop() : '');
    
    // Determine final attachment URL (prioritize deliverable file for deliverable messages)
    const finalAttachmentUrl = (isDeliverable || isDeliverableApproved || isDeliverableRevision) && deliverableFileUrl 
      ? deliverableFileUrl 
      : attachmentUrl;
    const finalFileName = (isDeliverable || isDeliverableApproved || isDeliverableRevision) && deliverableFileName 
      ? deliverableFileName 
      : fileName;
    
    const hasDeliverableOrAttachment = hasAttachment || ((isDeliverable || isDeliverableApproved || isDeliverableRevision) && deliverableFileUrl);
    
    const isImage = message.message_type === 'image' || 
      (finalAttachmentUrl && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(finalAttachmentUrl)) ||
      (finalFileName && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(finalFileName));
    const isAudio = message.message_type === 'voice' || 
      (finalAttachmentUrl && /\.(mp3|wav|ogg|webm|m4a)$/i.test(finalAttachmentUrl)) ||
      (finalFileName && /\.(mp3|wav|ogg|webm|m4a)$/i.test(finalFileName));
    
    return (
      <div className={cn("flex mb-3 overflow-hidden", isOwn ? "justify-end" : "justify-start")}>
        {!isOwn && (
          <Avatar 
            src={message.sender?.avatar_url} 
            name={message.sender?.name || message.sender_name} 
            size="sm" 
            className="mr-2 mt-1 shrink-0"
          />
        )}
        
        <div className={cn(
          "max-w-[85%] sm:max-w-[70%] rounded-2xl px-3 py-2 overflow-hidden",
          "break-words [word-break:break-word]",
          isDeliverable && "border-2 border-amber-300 bg-amber-50",
          isDeliverableApproved && "border-2 border-emerald-300 bg-emerald-50",
          isDeliverableRevision && "border-2 border-orange-300 bg-orange-50",
          !isDeliverable && !isDeliverableApproved && !isDeliverableRevision && (
            isOwn 
              ? `${colors.bg} text-white rounded-br-sm` 
              : "bg-slate-100 text-slate-800 rounded-bl-sm"
          )
        )}>
          {!isOwn && (
            <p className={cn(
              "text-xs font-medium mb-1",
              isDeliverable || isDeliverableApproved || isDeliverableRevision ? "text-slate-600" : "opacity-70"
            )}>
              {message.sender?.name || message.sender_name}
            </p>
          )}
          
          {/* Deliverable badge */}
          {isDeliverable && deliverable && (
            <div className="mb-2 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-amber-500 text-white">
                  <Package className="w-3 h-3 mr-1" />
                  Livrable v{deliverable.version || 1}
                </Badge>
                {(deliverable.status === 'pending' || deliverable.status === 'pending_agency') && (
                  <Badge variant="outline" className="border-amber-400 text-amber-700">
                    {deliverable.status === 'pending_agency' ? 'En attente agence' : 'En attente'}
                  </Badge>
                )}
                {deliverable.status === 'pending_client' && (
                  <Badge variant="outline" className="border-blue-400 text-blue-700">En attente client</Badge>
                )}
                {deliverable.agency_validated && !deliverable.client_validated && (
                  <Badge className="bg-sky-100 text-sky-700">✓ Agence</Badge>
                )}
                {deliverable.status === 'approved' && (
                  <Badge className="bg-emerald-500 text-white">Validé ✓</Badge>
                )}
                {deliverable.status === 'revision_requested' && (
                  <Badge className="bg-orange-500 text-white">Modifications demandées</Badge>
                )}
              </div>
              {/* Show associated task */}
              {deliverable.task_name && (
                <div className="flex items-center gap-1.5 text-xs">
                  <ClipboardCheck className="w-3.5 h-3.5 text-indigo-500" />
                  <span className="text-slate-600">Tâche :</span>
                  <span className="font-medium text-indigo-700">{deliverable.task_name}</span>
                  {deliverable.status === 'approved' && (
                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">Clôturée</Badge>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Attachment display - including deliverable files */}
          {hasDeliverableOrAttachment && finalAttachmentUrl && (
            <div className="mb-2">
              {isImage ? (
                <div className="relative group">
                  <img 
                    src={getFullUrl(finalAttachmentUrl)} 
                    alt={isDeliverable ? "Livrable" : "Image partagée"} 
                    className="max-w-full rounded-lg max-h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => {
                      setPreviewImage({
                        url: getFullUrl(finalAttachmentUrl),
                        name: finalFileName,
                        deliverable: isDeliverable ? deliverable : null,
                        message: message
                      });
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Overlay with zoom icon */}
                  <div 
                    className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center cursor-pointer"
                    onClick={() => {
                      setPreviewImage({
                        url: getFullUrl(finalAttachmentUrl),
                        name: finalFileName,
                        deliverable: isDeliverable ? deliverable : null,
                        message: message
                      });
                    }}
                  >
                    <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="hidden items-center gap-2 p-2 rounded-lg bg-white/70 border border-slate-200">
                    <FileText className="w-5 h-5 text-slate-600" />
                    <span className="text-sm truncate flex-1">{finalFileName || 'Image'}</span>
                  </div>
                </div>
              ) : isAudio ? (
                <div className="p-2 rounded-lg bg-white/70 border border-slate-200">
                  <div className="flex items-center gap-2 mb-1">
                    <Mic className="w-4 h-4 text-indigo-500" />
                    <span className="text-xs font-medium text-slate-600">Message vocal</span>
                  </div>
                  <audio 
                    controls 
                    src={getFullUrl(finalAttachmentUrl)}
                    className="w-full max-w-[280px]"
                  />
                  
                  {/* Show transcription if exists */}
                  {message.audio_transcript && (
                    <div className="mt-2 p-2 bg-slate-100 rounded-lg border-l-2 border-indigo-400">
                      <p className="text-xs font-medium text-slate-500 mb-1">Transcription :</p>
                      <p className="text-sm text-slate-700">{message.audio_transcript}</p>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 mt-2">
                    {!message.audio_transcript && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 gap-1"
                        data-testid={`transcribe-btn-${message.id}`}
                        onClick={async () => {
                          try {
                            toast.loading('Transcription en cours...', { id: `transcribe-${message.id}` });
                            
                            // Call API to transcribe and save
                            const transcribeRes = await api.post(`/projects/${projectId}/chat/messages/${message.id}/transcribe`);
                            toast.dismiss(`transcribe-${message.id}`);
                            
                            if (transcribeRes.data?.transcript) {
                              toast.success('Transcription ajoutée');
                              // Refresh messages to show transcription
                              fetchMessages(conversationId);
                            } else {
                              toast.error('Aucun texte détecté');
                            }
                          } catch (error) {
                            toast.dismiss(`transcribe-${message.id}`);
                            console.error('Transcription error:', error);
                            toast.error(error.response?.data?.detail || 'Erreur lors de la transcription');
                          }
                        }}
                      >
                        <FileText className="w-3 h-3" />
                        Transcrire
                      </Button>
                    )}
                    <a 
                      href={getFullUrl(finalAttachmentUrl)} 
                      download={finalFileName || 'audio.webm'}
                      className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" />
                      Télécharger
                    </a>
                  </div>
                </div>
              ) : (
                <a 
                  href={getFullUrl(finalAttachmentUrl)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={cn(
                    "flex items-center gap-2 p-2 rounded-lg transition-colors",
                    isDeliverable || isDeliverableApproved || isDeliverableRevision 
                      ? "bg-white/70 hover:bg-white border border-slate-200" 
                      : isOwn ? "bg-white/20 hover:bg-white/30" : "bg-slate-200 hover:bg-slate-300"
                  )}
                >
                  <FileText className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm truncate flex-1">
                    {finalFileName || 'Fichier'}
                  </span>
                  <Download className="w-4 h-4 flex-shrink-0" />
                </a>
              )}
            </div>
          )}
          
          {/* Text content */}
          {message.content && (
            <p className={cn(
              "text-sm whitespace-pre-wrap break-words",
              (isDeliverable || isDeliverableApproved || isDeliverableRevision) && "text-slate-800"
            )}>
              {renderTextWithLinks(message.content)}
            </p>
          )}
          
          {/* Validation buttons - Agency validation (for pending_agency) */}
          {isDeliverable && !isClient && deliverable && (deliverable.status === 'pending_agency' || (deliverable.status === 'pending' && !deliverable.agency_validated)) && (
            <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700 mb-2 font-medium">⏳ En attente de votre validation agence</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-sky-500 hover:bg-sky-600 text-white flex-1"
                  onClick={() => handleDeliverableAction(message.deliverable_id, 'agency-validate')}
                  data-testid="btn-agency-validate-deliverable"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Valider & Envoyer au client
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-orange-400 text-orange-700 hover:bg-orange-50"
                  onClick={() => {
                    setFeedbackDeliverableId(message.deliverable_id);
                    setShowFeedbackModal(true);
                  }}
                  data-testid="btn-agency-request-changes"
                >
                  <ThumbsDown className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Validation buttons for client - only on pending_client deliverables (after agency validation) */}
          {isDeliverable && isClient && deliverable && (deliverable.status === 'pending_client' || (deliverable.status === 'pending' && deliverable.visible_to_client)) && (
            <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 mb-2 font-medium">✅ Validé par l&apos;agence - En attente de votre validation</p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white flex-1"
                  onClick={() => handleDeliverableAction(message.deliverable_id, 'approve')}
                  data-testid="btn-approve-deliverable"
                >
                  <ThumbsUp className="w-4 h-4 mr-1" />
                  Valider & Clôturer
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-orange-400 text-orange-700 hover:bg-orange-50 flex-1"
                  onClick={() => {
                    setFeedbackDeliverableId(message.deliverable_id);
                    setShowFeedbackModal(true);
                  }}
                  data-testid="btn-request-changes"
                >
                  <ThumbsDown className="w-4 h-4 mr-1" />
                  Modifications
                </Button>
              </div>
            </div>
          )}
          
          {/* Cancel agency validation button - Agency can cancel before client validates */}
          {isDeliverable && !isClient && deliverable && deliverable.status === 'pending_client' && deliverable.agency_validated && !deliverable.client_validated && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => {
                  if (window.confirm('Annuler la validation agence ? Le livrable ne sera plus visible par le client.')) {
                    handleDeliverableAction(message.deliverable_id, 'cancel-agency-validation');
                  }
                }}
                data-testid="btn-cancel-agency-validation"
              >
                <X className="w-3 h-3 mr-1" />
                Annuler ma validation
              </Button>
            </div>
          )}
          
          {/* Cancel client validation button - Both agency and client can cancel after approval */}
          {isDeliverable && deliverable && deliverable.status === 'approved' && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="ghost"
                className="text-xs text-slate-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => {
                  if (window.confirm('Annuler la validation ? Le livrable reviendra en attente de validation client.')) {
                    handleDeliverableAction(message.deliverable_id, 'cancel-client-validation');
                  }
                }}
                data-testid="btn-cancel-client-validation"
              >
                <X className="w-3 h-3 mr-1" />
                Annuler la validation
              </Button>
            </div>
          )}
          
          <div className={cn(
            "flex items-center justify-end gap-1 mt-1",
            (isDeliverable || isDeliverableApproved || isDeliverableRevision) ? "text-slate-400" : (isOwn ? "text-white/70" : "text-slate-400")
          )}>
            <span className="text-[10px]">
              {new Date(message.created_at).toLocaleTimeString('fr-FR', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
            <ReadIndicator message={message} isOwnMessage={isOwn} />
          </div>
        </div>
      </div>
    );
  };

  // File selection handler
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux (max 50 Mo)');
        return;
      }
      setSelectedFile(file);
    }
  };

  // Clear selected file
  const clearSelectedFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Helper function to render text with clickable links
  const renderTextWithLinks = (text) => {
    if (!text) return null;
    
    // Regex to match URLs (http, https, www)
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;
    
    // Split text by URLs and map to elements
    const parts = text.split(urlRegex);
    const matches = text.match(urlRegex) || [];
    
    let matchIndex = 0;
    const result = [];
    
    parts.forEach((part, index) => {
      if (part) {
        // Check if this part is a URL
        if (urlRegex.test(part)) {
          const href = part.startsWith('www.') ? `https://${part}` : part;
          result.push(
            <a
              key={`link-${index}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {part}
            </a>
          );
        } else {
          result.push(<span key={`text-${index}`}>{part}</span>);
        }
      }
    });
    
    return result.length > 0 ? result : text;
  };

  // Fetch project tasks for completion
  const fetchProjectTasks = async (projectId) => {
    try {
      const res = await api.get(`/tasks/project/${projectId}`);
      // Filter to show only in-progress or pending tasks
      const activeTasks = (res.data || []).filter(t => 
        t.status === 'in_progress' || t.status === 'pending' || t.status === 'todo'
      );
      setProjectTasks(activeTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setProjectTasks([]);
    }
  };

  // Complete a task from chat
  const handleCompleteTask = async (taskId) => {
    if (!activeProjectConv || !taskId) return;
    
    setCompletingTask(true);
    try {
      await api.put(`/tasks/${taskId}`, {
        status: 'completed'
      });
      
      // Send a message to the chat
      await api.post(`/projects/${activeProjectConv.id}/messages`, {
        content: `✅ Tâche terminée !`,
        message_type: 'system'
      });
      
      toast.success('Tâche marquée comme terminée');
      setShowTaskModal(false);
      fetchProjectMessages(activeProjectConv.id);
      fetchProjectTasks(activeProjectConv.id);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setCompletingTask(false);
    }
  };

  // Send a deliverable/version to client for validation
  const handleSendDeliverable = async () => {
    if (!activeProjectConv || !deliverableTitle.trim()) {
      toast.error('Veuillez saisir un titre pour le livrable');
      return;
    }
    
    // Validation selon le type
    if ((deliverableType === 'link_web' || deliverableType === 'link_video') && !deliverableLink.trim()) {
      toast.error('Veuillez saisir un lien');
      return;
    }
    if ((deliverableType === 'image' || deliverableType === 'pdf') && !deliverableFile) {
      toast.error('Veuillez joindre un fichier');
      return;
    }
    
    setSendingDeliverable(true);
    try {
      let fileUrl = null;
      let fileName = null;
      
      // Upload file if present (for image/pdf types)
      if (deliverableFile && (deliverableType === 'image' || deliverableType === 'pdf')) {
        const formData = new FormData();
        formData.append('file', deliverableFile);
        const uploadRes = await api.post(`/projects/${activeProjectConv.id}/chat/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        fileUrl = uploadRes.data.file_url;
        fileName = deliverableFile.name;
      }
      
      // For link types, use the link as the file_url
      if (deliverableType === 'link_web' || deliverableType === 'link_video') {
        fileUrl = deliverableLink;
        fileName = deliverableType === 'link_web' ? 'Lien web' : 'Lien vidéo';
      }
      
      // Create deliverable with task association, version info and type
      await api.post(`/projects/${activeProjectConv.id}/deliverables`, {
        name: deliverableTitle,
        description: deliverableComment,
        file_url: fileUrl,
        file_name: fileName,
        deliverable_type: deliverableType,
        task_id: selectedTaskForDeliverable === 'none' ? null : (selectedTaskForDeliverable || null),
        is_new_version: isNewVersion,
        previous_deliverable_id: isNewVersion ? previousDeliverableId : null
      });
      
      toast.success('Livrable envoyé au client pour validation');
      setShowDeliverableModal(false);
      setDeliverableTitle('');
      setDeliverableComment('');
      setDeliverableFile(null);
      setDeliverableLink('');
      setDeliverableType('file');
      setSelectedTaskForDeliverable('');
      setIsNewVersion(false);
      setPreviousDeliverableId('');
      fetchProjectMessages(activeProjectConv.id);
      fetchProjectDeliverables(activeProjectConv.id);
    } catch (error) {
      console.error('Error sending deliverable:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingDeliverable(false);
    }
  };

  // Client validates or requests changes on a deliverable
  const handleDeliverableAction = async (deliverableId, action, feedback = '') => {
    if (!activeProjectConv) return;
    
    try {
      await api.post(`/projects/${activeProjectConv.id}/deliverables/${deliverableId}/${action}`, {
        feedback
      });
      
      const messages = {
        'approve': 'Livrable validé par le client !',
        'agency-validate': 'Livrable validé par l\'agence et envoyé au client !',
        'reject': 'Demande de modifications envoyée',
        'cancel-agency-validation': 'Validation agence annulée',
        'cancel-client-validation': 'Validation client annulée'
      };
      
      toast.success(messages[action] || 'Action effectuée');
      setShowFeedbackModal(false);
      setFeedbackDeliverableId(null);
      setFeedbackText('');
      fetchProjectMessages(activeProjectConv.id);
      fetchProjectDeliverables(activeProjectConv.id);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'action';
      toast.error(errorMsg);
    }
  };

  // Fetch project deliverables
  const fetchProjectDeliverables = async (projectId) => {
    try {
      const res = await api.get(`/projects/${projectId}/deliverables`);
      setProjectDeliverables(res.data || []);
    } catch (error) {
      console.error('Error fetching deliverables:', error);
      setProjectDeliverables([]);
    }
  };

  // Audio recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.success('Enregistrement démarré');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Impossible d\'accéder au microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setShowAudioOptions(true);
    }
  };

  const cancelRecording = () => {
    setAudioBlob(null);
    setShowAudioOptions(false);
  };

  // Send audio as file - supports all conversation types
  const sendAudioMessage = async () => {
    if (!audioBlob) {
      toast.error('Aucun enregistrement audio');
      return;
    }
    
    // Determine which conversation is active
    let convType = null;
    let convId = null;
    let apiEndpoint = null;
    
    if (activeProjectConv) {
      convType = 'project';
      convId = activeProjectConv.id;
      apiEndpoint = `/projects/${convId}/chat/upload`;
    } else if (activeClientConv) {
      convType = 'client';
      convId = activeClientConv.id;
      apiEndpoint = `/client-chat/conversations/${convId}/upload`;
    } else if (activeInternalConv) {
      convType = 'internal';
      convId = activeInternalConv.id;
      apiEndpoint = `/internal-chat/conversations/${convId}/upload`;
    } else {
      toast.error('Veuillez sélectionner une conversation');
      return;
    }
    
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio-message.webm');
      formData.append('message', '🎤 Message vocal');
      
      await api.post(apiEndpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAudioBlob(null);
      setShowAudioOptions(false);
      toast.success('Message vocal envoyé');
      
      // Refresh messages based on conversation type
      if (convType === 'project') {
        fetchProjectMessages(convId);
      } else if (convType === 'client') {
        fetchClientMessages(convId);
      } else if (convType === 'internal') {
        fetchInternalMessages(convId);
      }
    } catch (error) {
      console.error('Send audio error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du message vocal');
    } finally {
      setSending(false);
    }
  };

  // Transcribe audio to text using Whisper
  const transcribeAudio = async () => {
    if (!audioBlob) {
      toast.error('Aucun enregistrement audio');
      return;
    }
    
    setIsTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'audio.webm');
      
      const res = await api.post('/audio/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const transcribedText = res.data.text || '';
      if (!transcribedText) {
        toast.warning('Aucun texte détecté dans l\'audio');
        return;
      }
      setNewMessage(prev => prev + (prev ? ' ' : '') + transcribedText);
      setAudioBlob(null);
      setShowAudioOptions(false);
      toast.success('Transcription terminée');
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Empty state component
  const EmptyState = ({ icon: Icon, title, description, action }) => (
    <div className="flex flex-col items-center justify-center h-full p-8 text-center">
      <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
        <Icon className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="font-medium text-slate-700 mb-1">{title}</h3>
      <p className="text-sm text-slate-500 mb-4">{description}</p>
      {action}
    </div>
  );

  // Back button handler for mobile - returns to conversation list
  const handleMobileBack = () => {
    setMobileShowChat(false);
    setMobileFullscreen(false);
  };

  // Check if we should render fullscreen mode
  const shouldShowFullscreen = isMobile && mobileFullscreen && mobileShowChat;

  // Get current conversation info for fullscreen header
  const getFullscreenHeader = () => {
    if (activeTab === 'internal' && activeInternalConv) {
      const otherUser = activeInternalConv.participants?.find(p => p.id !== user?.id);
      return {
        title: otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : (activeInternalConv.name || 'Chat'),
        subtitle: 'Chat interne',
        deliverableCount: 0
      };
    }
    if (activeTab === 'projects' && activeProjectConv) {
      // Count deliverables and their versions
      const totalDeliverables = projectDeliverables.length;
      const pendingCount = projectDeliverables.filter(d => d.status === 'pending_agency' || d.status === 'pending_client').length;
      const approvedCount = projectDeliverables.filter(d => d.status === 'approved').length;
      
      let deliverableInfo = '';
      if (totalDeliverables > 0) {
        deliverableInfo = `${totalDeliverables} livrable${totalDeliverables > 1 ? 's' : ''}`;
        if (pendingCount > 0) {
          deliverableInfo += ` (${pendingCount} en attente)`;
        }
      }
      
      return {
        title: activeProjectConv.project_name || activeProjectConv.name || 'Projet',
        subtitle: activeProjectConv.client_name || 'Chat projet',
        deliverableInfo,
        deliverableCount: totalDeliverables,
        pendingCount,
        approvedCount
      };
    }
    if ((activeTab === 'clients' || activeTab === 'agency') && activeClientConv) {
      const isAgencyView = !isClient;
      return {
        title: isAgencyView 
          ? (activeClientConv.client_name || activeClientConv.company_name || 'Client')
          : 'Agence MSC',
        subtitle: isAgencyView ? 'Chat client' : 'Support',
        deliverableCount: 0
      };
    }
    return { title: 'Chat', subtitle: '', deliverableCount: 0 };
  };

  return (
    <>
      {/* Mobile Fullscreen Chat Overlay */}
      {shouldShowFullscreen && (
        <div className="fixed inset-0 z-50 bg-white flex flex-col overflow-hidden">
          {/* Header bar like WhatsApp */}
          <div className="flex items-center gap-3 px-3 py-2 bg-slate-100 border-b shadow-sm shrink-0">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleMobileBack}
              className="h-9 w-9 shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0 overflow-hidden">
              <h2 className="font-semibold text-sm truncate">{getFullscreenHeader().title}</h2>
              <div className="flex items-center gap-2">
                <p className="text-xs text-slate-500 truncate">{getFullscreenHeader().subtitle}</p>
                {getFullscreenHeader().deliverableCount > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium whitespace-nowrap">
                    📦 {getFullscreenHeader().deliverableCount} livrable{getFullscreenHeader().deliverableCount > 1 ? 's' : ''}
                    {getFullscreenHeader().pendingCount > 0 && (
                      <span className="ml-1 text-orange-600">({getFullscreenHeader().pendingCount} en attente)</span>
                    )}
                  </span>
                )}
              </div>
            </div>
            {/* Project action buttons */}
            {activeTab === 'projects' && activeProjectConv && !isClient && (
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={() => setShowDeliverableModal(true)}
                >
                  <Package className="w-3 h-3 mr-1" />
                  Livrable
                </Button>
              </div>
            )}
          </div>
          
          {/* Selected file preview */}
          {selectedFile && (
            <div className="px-3 py-2 bg-sky-50 border-b flex items-center gap-2 shrink-0">
              <FileText className="w-4 h-4 text-sky-600 shrink-0" />
              <span className="text-sm text-sky-700 truncate flex-1">{selectedFile.name}</span>
              <span className="text-xs text-sky-500 shrink-0">
                {(selectedFile.size / 1024).toFixed(0)} Ko
              </span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 text-sky-600 hover:text-sky-800 shrink-0"
                onClick={clearSelectedFile}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {/* Audio recording preview */}
          {showAudioOptions && audioBlob && (
            <div className="px-3 py-2 bg-amber-50 border-b flex items-center gap-2 shrink-0">
              <Mic className="w-4 h-4 text-amber-600 shrink-0" />
              <span className="text-sm text-amber-700 flex-1">Message vocal enregistré</span>
              <Button 
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => {
                  if (activeTab === 'internal') handleSendInternalMessage();
                  else if (activeTab === 'projects') handleSendProjectMessage();
                  else handleSendClientMessage();
                }}
                disabled={sending}
              >
                Envoyer
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={transcribeAudio}
                disabled={isTranscribing}
              >
                {isTranscribing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                Transcrire
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6"
                onClick={() => { setAudioBlob(null); setShowAudioOptions(false); }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {/* Messages ScrollArea - with proper responsive padding */}
          <ScrollArea className="flex-1 bg-slate-50">
            <div className="p-3 space-y-2 overflow-x-hidden">
              {activeTab === 'internal' && internalMessages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isOwn={msg.sender_id === user?.id}
                  chatType="internal"
                />
              ))}
              {activeTab === 'projects' && projectMessages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isOwn={msg.sender_id === user?.id}
                  chatType="project"
                />
              ))}
              {(activeTab === 'clients' || activeTab === 'agency') && clientMessages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isOwn={msg.sender_id === user?.id}
                  chatType="client"
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>
          
          {/* Input Area - Full featured */}
          <div className="p-2 border-t bg-white shrink-0">
            <div className="flex items-center gap-1">
              {/* File attachment */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              />
              <Button
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                className="h-9 w-9 shrink-0"
              >
                <Paperclip className="w-5 h-5 text-slate-500" />
              </Button>
              
              {/* Audio recording button with REC indicator */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={isRecording ? stopRecording : startRecording}
                  className={cn(
                    "h-9 w-9 shrink-0 transition-all",
                    isRecording && "bg-red-100 text-red-600 ring-2 ring-red-400 ring-offset-1"
                  )}
                  title={isRecording ? "Arrêter l'enregistrement" : "Enregistrer un message vocal"}
                >
                  {isRecording ? (
                    <MicOff className="w-5 h-5 text-red-600" />
                  ) : (
                    <Mic className="w-5 h-5 text-slate-500" />
                  )}
                </Button>
                {/* REC badge when recording */}
                {isRecording && (
                  <span className="absolute -top-2 -right-2 flex items-center gap-1 px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full animate-pulse">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                    REC
                  </span>
                )}
              </div>
              
              {/* Recording indicator bar */}
              {isRecording && (
                <div className="flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-full">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-xs font-medium text-red-600">Enregistrement...</span>
                </div>
              )}
              
              {/* Message input */}
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isRecording ? "🎤 Enregistrement en cours..." : "Votre message..."}
                className={cn("flex-1 h-9 text-sm", isRecording && "bg-red-50 border-red-200")}
                disabled={isRecording}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (activeTab === 'internal') handleSendInternalMessage();
                    else if (activeTab === 'projects') handleSendProjectMessage();
                    else handleSendClientMessage();
                  }
                }}
              />
              
              {/* Send button */}
              <Button
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => {
                  if (activeTab === 'internal') handleSendInternalMessage();
                  else if (activeTab === 'projects') handleSendProjectMessage();
                  else handleSendClientMessage();
                }}
                disabled={sending || (!newMessage.trim() && !selectedFile && !audioBlob)}
                style={{ backgroundColor: theme.colors?.accent }}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Normal Layout (hidden when fullscreen on mobile) */}
      <div className={cn(shouldShowFullscreen && "hidden")}>
        <MainLayout>
      <div className="h-[calc(100vh-120px)] md:h-[calc(100vh-120px)] flex flex-col px-2 md:px-0">
        {/* Header - Hidden on mobile when in fullscreen chat */}
        <div className={cn(
          "flex items-center justify-between mb-2 md:mb-4",
          mobileShowChat && "hidden md:flex"
        )}>
          <div>
            <h1 className="text-xl md:text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              Messagerie
            </h1>
            <p className="text-xs md:text-sm text-slate-500">
              Conversations internes, projets et clients
            </p>
          </div>
        </div>

        {/* Chat Container */}
        <Card className={cn(
          "card flex-1 overflow-hidden",
          mobileShowChat && "md:rounded-lg rounded-none"
        )}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
            {/* Tabs Header - Hidden on mobile when showing fullscreen chat */}
            <div className={cn(
              "border-b px-2 md:px-4 pt-2 md:pt-3 pb-2",
              mobileShowChat && "hidden md:block"
            )}>
              {/* Client view: only Agency and Projects tabs */}
              {isClient ? (
                <TabsList className="grid grid-cols-2 w-full h-auto gap-1 p-1 bg-slate-100 rounded-lg">
                  <TabsTrigger 
                    value="agency" 
                    className="relative py-3 px-3 min-h-[48px] text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm touch-manipulation" 
                    data-testid="tab-agency"
                    onClick={() => setActiveTab('agency')}
                  >
                    <Building2 className="w-4 h-4 mr-2 shrink-0" />
                    <span className="truncate">Chat Agence</span>
                    {unreadCounts.clients > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-sky-400 text-white text-[10px] px-1.5 py-0 min-w-[18px]">
                        {unreadCounts.clients}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="projects" 
                    className="relative py-3 px-3 min-h-[48px] text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm touch-manipulation" 
                    data-testid="tab-projects"
                    onClick={() => setActiveTab('projects')}
                  >
                    <FolderOpen className="w-4 h-4 mr-2 shrink-0" />
                    <span className="truncate">Projets</span>
                    {unreadCounts.projects > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-sky-400 text-white text-[10px] px-1.5 py-0 min-w-[18px]">
                        {unreadCounts.projects}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              ) : (
                /* Agency/Team view: Internal, Projects, and Clients tabs - Mobile optimized */
                <TabsList className="grid grid-cols-3 w-full h-auto gap-1 p-1 bg-slate-100 rounded-lg">
                  <TabsTrigger 
                    value="internal" 
                    className={cn(
                      "relative py-2.5 md:py-3 px-2 md:px-3 min-h-[44px] md:min-h-[48px] text-xs md:text-sm font-medium rounded-md touch-manipulation",
                      "data-[state=active]:bg-white data-[state=active]:shadow-sm",
                      activeTab === 'internal' && "border-b-2 border-sky-400"
                    )} 
                    data-testid="tab-internal"
                    onClick={() => setActiveTab('internal')}
                  >
                    <Users className="w-4 h-4 mr-1 shrink-0 text-sky-600" />
                    <span className="truncate">Interne</span>
                    {unreadCounts.internal > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-sky-400 text-white text-[10px] px-1 py-0 min-w-[16px]">
                        {unreadCounts.internal}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="projects" 
                    className={cn(
                      "relative py-2.5 md:py-3 px-2 md:px-3 min-h-[44px] md:min-h-[48px] text-xs md:text-sm font-medium rounded-md touch-manipulation",
                      "data-[state=active]:bg-white data-[state=active]:shadow-sm",
                      activeTab === 'projects' && "border-b-2 border-fuchsia-400"
                    )} 
                    data-testid="tab-projects"
                    onClick={() => setActiveTab('projects')}
                  >
                    <FolderOpen className="w-4 h-4 mr-1 shrink-0 text-fuchsia-600" />
                    <span className="truncate">Projets</span>
                    {unreadCounts.projects > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-fuchsia-400 text-white text-[10px] px-1 py-0 min-w-[16px]">
                        {unreadCounts.projects}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="clients" 
                    className={cn(
                      "relative py-2.5 md:py-3 px-2 md:px-3 min-h-[44px] md:min-h-[48px] text-xs md:text-sm font-medium rounded-md touch-manipulation",
                      "data-[state=active]:bg-white data-[state=active]:shadow-sm",
                      activeTab === 'clients' && "border-b-2 border-teal-400"
                    )} 
                    data-testid="tab-clients"
                    onClick={() => setActiveTab('clients')}
                  >
                    <Building2 className="w-4 h-4 mr-1 shrink-0 text-teal-600" />
                    <span className="truncate">Clients</span>
                    {unreadCounts.clients > 0 && (
                      <Badge className="absolute -top-1 -right-1 bg-teal-400 text-white text-[10px] px-1 py-0 min-w-[16px]">
                        {unreadCounts.clients}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              )}
            </div>

            {/* Internal Chat Tab - Agency/Team only */}
            {!isClient && (
            <TabsContent value="internal" className="flex-1 m-0 overflow-hidden">
              <div className="flex h-full">
                {/* Conversations List */}
                <div className={cn(
                  "w-full md:w-80 border-r flex flex-col",
                  mobileShowChat && "hidden md:flex"
                )}>
                  {/* Search */}
                  <div className="p-2 md:p-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Rechercher..."
                        className="pl-10 h-9 md:h-10 text-sm"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    <Button 
                      className="w-full mt-2"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowNewConversation(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Nouvelle conversation
                    </Button>
                  </div>
                  
                  {/* Conversations */}
                  <ScrollArea className="flex-1">
                    <div className="p-2 space-y-1">
                      {internalConversations
                        .filter(c => !searchQuery || c.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                        .map(conv => (
                          <ConversationItem
                            key={conv.id}
                            conv={conv}
                            isActive={activeInternalConv?.id === conv.id}
                            onClick={() => handleSelectInternalConv(conv)}
                            type="internal"
                          />
                        ))
                      }
                      
                      {internalConversations.length === 0 && !loading && (
                        <div className="text-center py-8 text-slate-500 text-sm">
                          Aucune conversation
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Messages Area */}
                <div className={cn(
                  "flex-1 flex flex-col",
                  !mobileShowChat && "hidden md:flex"
                )}>
                  {activeInternalConv ? (
                    <>
                      {/* Chat Header */}
                      <div className="p-4 border-b flex items-center gap-3">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="md:hidden"
                          onClick={() => setMobileShowChat(false)}
                        >
                          <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <Avatar 
                          src={activeInternalConv.avatar_url} 
                          name={activeInternalConv.name} 
                          size="md"
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold">{activeInternalConv.name}</h3>
                          <p className="text-xs text-slate-500">
                            {activeInternalConv.is_group ? `${activeInternalConv.members?.length || 0} membres` : 'En ligne'}
                          </p>
                        </div>
                      </div>
                      
                      {/* Messages */}
                      <ScrollArea className="flex-1 p-4">
                        {internalMessages.map((msg) => (
                          <MessageBubble
                            key={msg.id}
                            message={msg}
                            isOwn={msg.sender_id === user?.id}
                            chatType="internal"
                          />
                        ))}
                        <div ref={messagesEndRef} />
                      </ScrollArea>
                      
                      {/* Selected file preview */}
                      {selectedFile && activeTab === 'internal' && (
                        <div className="px-4 py-2 bg-sky-50 border-t border-sky-200 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-sky-600" />
                          <span className="text-sm text-sky-700 truncate flex-1">{selectedFile.name}</span>
                          <span className="text-xs text-sky-500">
                            {(selectedFile.size / 1024).toFixed(0)} Ko
                          </span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-sky-600 hover:text-sky-800"
                            onClick={clearSelectedFile}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Audio recording preview */}
                      {showAudioOptions && audioBlob && activeTab === 'internal' && (
                        <div className="px-4 py-3 bg-sky-50 border-t border-sky-200 flex items-center gap-3">
                          <div className="flex items-center gap-2 flex-1">
                            <Mic className="w-5 h-5 text-sky-600" />
                            <span className="text-sm text-sky-700">Message vocal enregistré</span>
                          </div>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={handleSendInternalMessage}
                            disabled={sending}
                            className="border-sky-300 text-sky-700 hover:bg-sky-100"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Envoyer audio
                          </Button>
                          <Button 
                            size="sm"
                            onClick={transcribeAudio}
                            disabled={isTranscribing}
                            className="bg-sky-500 hover:bg-sky-600 text-white"
                          >
                            {isTranscribing ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4 mr-1" />
                            )}
                            Transcrire
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-slate-500 hover:text-slate-700"
                            onClick={cancelRecording}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Input with blue theme */}
                      <div className="p-4 border-t border-blue-100 flex items-center gap-2">
                        {/* File input */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                          className="hidden"
                          accept="*/*"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-sky-600 hover:text-sky-800 hover:bg-sky-100"
                          title="Joindre un fichier"
                        >
                          <Paperclip className="w-5 h-5" />
                        </Button>
                        
                        {/* Audio recording button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={isRecording ? stopRecording : startRecording}
                          className={cn(
                            isRecording 
                              ? "text-red-500 hover:text-red-700 hover:bg-red-100 animate-pulse" 
                              : "text-sky-600 hover:text-sky-800 hover:bg-sky-100"
                          )}
                          title={isRecording ? "Arrêter l'enregistrement" : "Message vocal / Dictée"}
                        >
                          <Mic className="w-5 h-5" />
                        </Button>
                        
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={isRecording ? "Enregistrement en cours..." : "Votre message..."}
                          className="flex-1 border-blue-200 focus:border-blue-400"
                          disabled={isRecording}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendInternalMessage();
                            }
                          }}
                        />
                        <Button 
                          onClick={handleSendInternalMessage}
                          disabled={(!newMessage.trim() && !selectedFile && !audioBlob) || sending || isRecording}
                          size="icon"
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <EmptyState 
                      icon={MessageSquare}
                      title="Sélectionnez une conversation"
                      description="Choisissez une conversation dans la liste ou créez-en une nouvelle"
                    />
                  )}
                </div>
              </div>
            </TabsContent>
            )}

            {/* Agency Chat Tab - Client only (to chat with agency members) */}
            {isClient && (
            <TabsContent value="agency" className="flex-1 m-0 overflow-hidden">
              <div className="flex h-full">
                <div className={cn(
                  "w-full md:w-80 border-r flex flex-col",
                  mobileShowChat && "hidden md:flex"
                )}>
                  <div className="p-4 border-b">
                    <h3 className="font-semibold text-slate-700">Contacter l&apos;agence</h3>
                    <p className="text-xs text-slate-500 mt-1">
                      Échangez avec votre responsable de compte
                    </p>
                  </div>
                  
                  <ScrollArea className="flex-1">
                    <div className="p-2 space-y-1">
                      {clientConversations.map(conv => (
                        <ConversationItem
                          key={conv.id}
                          conv={{
                            ...conv,
                            name: conv.other_user?.name || 'Agence',
                            avatar_url: conv.other_user?.avatar_url
                          }}
                          isActive={activeClientConv?.id === conv.id}
                          onClick={() => handleSelectClientConv(conv)}
                          type="agency"
                        />
                      ))}
                      
                      {clientConversations.length === 0 && !loading && (
                        <div className="text-center py-8 text-slate-500 text-sm">
                          <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                          <p>Pas de conversation en cours</p>
                          <Button 
                            className="mt-3" 
                            size="sm"
                            style={{ backgroundColor: theme.colors?.accent }}
                            onClick={() => setShowNewConversation(true)}
                          >
                            Démarrer une conversation
                          </Button>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Messages Area */}
                <div className={cn(
                  "flex-1 flex flex-col",
                  !mobileShowChat && "hidden md:flex"
                )}>
                  {activeClientConv ? (
                    <>
                      {/* Chat Header */}
                      <div className="p-4 border-b flex items-center gap-3">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="md:hidden"
                          onClick={() => setMobileShowChat(false)}
                        >
                          <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <Avatar 
                          src={activeClientConv.other_user?.avatar_url} 
                          name={activeClientConv.other_user?.name || 'Agence'} 
                          size="md"
                        />
                        <div>
                          <h3 className="font-semibold">{activeClientConv.other_user?.name || 'Agence MSC'}</h3>
                          <p className="text-xs text-slate-500">Service client</p>
                        </div>
                      </div>

                      {/* Messages */}
                      <ScrollArea className="flex-1 p-4">
                        <div className="space-y-4">
                          {clientMessages.map(message => (
                            <MessageBubble 
                              key={message.id} 
                              message={message} 
                              isOwnMessage={message.sender_id === user?.id}
                            />
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      </ScrollArea>

                      {/* Selected file preview */}
                      {selectedFile && activeTab === 'agency' && (
                        <div className="px-4 py-2 bg-teal-50 border-t border-teal-200 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-teal-600" />
                          <span className="text-sm text-teal-700 truncate flex-1">{selectedFile.name}</span>
                          <span className="text-xs text-teal-500">
                            {(selectedFile.size / 1024).toFixed(0)} Ko
                          </span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-teal-600 hover:text-teal-800"
                            onClick={clearSelectedFile}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Audio recording preview */}
                      {showAudioOptions && audioBlob && activeTab === 'agency' && (
                        <div className="px-4 py-3 bg-teal-50 border-t border-teal-200 flex items-center gap-3">
                          <div className="flex items-center gap-2 flex-1">
                            <Mic className="w-5 h-5 text-teal-600" />
                            <span className="text-sm text-teal-700">Message vocal enregistré</span>
                          </div>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={handleSendClientMessage}
                            disabled={sending}
                            className="border-teal-300 text-teal-700 hover:bg-teal-100"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Envoyer audio
                          </Button>
                          <Button 
                            size="sm"
                            onClick={transcribeAudio}
                            disabled={isTranscribing}
                            className="bg-teal-500 hover:bg-teal-600 text-white"
                          >
                            {isTranscribing ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4 mr-1" />
                            )}
                            Transcrire
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-slate-500 hover:text-slate-700"
                            onClick={cancelRecording}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}

                      {/* Input - Add extra padding on mobile for client FAB */}
                      <div className={cn(
                        "p-4 border-t flex items-center gap-2",
                        isClient && "pb-20 md:pb-4"
                      )}>
                        {/* File input */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                          className="hidden"
                          accept="*/*"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-teal-600 hover:text-teal-800 hover:bg-teal-100"
                          title="Joindre un fichier"
                        >
                          <Paperclip className="w-5 h-5" />
                        </Button>
                        
                        {/* Audio recording button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={isRecording ? stopRecording : startRecording}
                          className={cn(
                            isRecording 
                              ? "text-red-500 hover:text-red-700 hover:bg-red-100 animate-pulse" 
                              : "text-teal-600 hover:text-teal-800 hover:bg-teal-100"
                          )}
                          title={isRecording ? "Arrêter l'enregistrement" : "Message vocal / Dictée"}
                        >
                          <Mic className="w-5 h-5" />
                        </Button>
                        
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={isRecording ? "Enregistrement en cours..." : "Votre message..."}
                          className="flex-1"
                          disabled={isRecording}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendClientMessage();
                            }
                          }}
                        />
                        <Button 
                          onClick={handleSendClientMessage}
                          disabled={(!newMessage.trim() && !selectedFile && !audioBlob) || sending || isRecording}
                          size="icon"
                          style={{ backgroundColor: theme.colors?.accent }}
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <EmptyState 
                      icon={Building2}
                      title="Chat Agence"
                      description="Sélectionnez ou démarrez une conversation avec l'agence"
                    />
                  )}
                </div>
              </div>
            </TabsContent>
            )}

            {/* Project Chat Tab */}
            <TabsContent value="projects" className="flex-1 m-0 overflow-hidden">
              <div className="flex h-full">
                {/* Projects List */}
                <div className={cn(
                  "w-full md:w-80 border-r flex flex-col",
                  mobileShowChat && "hidden md:flex"
                )}>
                  <div className="p-3 border-b">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input 
                        placeholder="Rechercher un projet..."
                        className="pl-10"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  
                  <ScrollArea className="flex-1">
                    <div className="p-2 space-y-1">
                      {projectConversations
                        .filter(c => !searchQuery || c.name?.toLowerCase().includes(searchQuery.toLowerCase()))
                        .map(conv => (
                          <ConversationItem
                            key={conv.id}
                            conv={{
                              ...conv,
                              name: conv.name,
                              avatar_url: conv.client_avatar
                            }}
                            isActive={activeProjectConv?.id === conv.id}
                            onClick={() => handleSelectProjectConv(conv)}
                            type="project"
                          />
                        ))
                      }
                      
                      {projectConversations.length === 0 && !loading && (
                        <div className="text-center py-8 text-slate-500 text-sm">
                          Aucun projet avec chat actif
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                {/* Messages Area */}
                <div className={cn(
                  "flex-1 flex flex-col",
                  !mobileShowChat && "hidden md:flex"
                )}>
                  {activeProjectConv ? (
                    <>
                      {/* Chat Header with client warning */}
                      <div className="p-4 border-b">
                        <div className="flex items-center gap-3">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="md:hidden"
                            onClick={() => setMobileShowChat(false)}
                          >
                            <ArrowLeft className="w-5 h-5" />
                          </Button>
                          <Avatar 
                            src={activeProjectConv.client_avatar} 
                            name={activeProjectConv.name} 
                            size="md"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold">{activeProjectConv.name}</h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-xs text-slate-500">
                                {activeProjectConv.client_name}
                                {activeProjectConv.deadline && (
                                  <span className="ml-2">
                                    • Deadline: {new Date(activeProjectConv.deadline).toLocaleDateString('fr-FR')}
                                  </span>
                                )}
                              </p>
                              {projectDeliverables.length > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                                  📦 {projectDeliverables.length} livrable{projectDeliverables.length > 1 ? 's' : ''}
                                  {projectDeliverables.filter(d => d.status === 'pending_agency' || d.status === 'pending_client').length > 0 && (
                                    <span className="ml-1 text-orange-600">
                                      ({projectDeliverables.filter(d => d.status === 'pending_agency' || d.status === 'pending_client').length} en attente)
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Team Members Avatars with rotating beam status indicators */}
                          <div className="flex items-center gap-2">
                            {activeProjectConv.team_members?.slice(0, 5).map((member, idx) => {
                              // Determine status
                              const getStatusInfo = () => {
                                if (member.has_active_chrono) {
                                  return { beamClass: 'beam-blue beam-fast', label: '⏱️ Chrono actif sur ce projet' };
                                }
                                if (member.has_chrono_elsewhere) {
                                  return { beamClass: 'beam-orange', label: '🔶 En travail sur autre projet' };
                                }
                                if (!member.is_available) {
                                  return { beamClass: '', label: '⚫ Indisponible' };
                                }
                                return { beamClass: 'beam-green beam-slow', label: '🟢 Disponible' };
                              };
                              
                              const status = getStatusInfo();
                              const hasBeam = status.beamClass.includes('beam-');
                              
                              return (
                                <div 
                                  key={member.id || idx} 
                                  className={cn(
                                    "relative group cursor-pointer",
                                    hasBeam && "avatar-beam-wrapper",
                                    status.beamClass
                                  )}
                                  style={{ 
                                    '--beam-size': '42px',
                                    padding: hasBeam ? '2px' : '0'
                                  }}
                                >
                                  <div className={cn(
                                    "w-9 h-9 rounded-full bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center text-white text-xs font-semibold transition-transform hover:scale-110",
                                    hasBeam && "avatar-beam-inner"
                                  )}>
                                    {member.avatar_url ? (
                                      <img src={member.avatar_url} alt={member.name} className="w-full h-full rounded-full object-cover" />
                                    ) : (
                                      <span>{member.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}</span>
                                    )}
                                  </div>
                                  {/* Tooltip on hover - fixed width to prevent cutoff */}
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-slate-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 pointer-events-none shadow-lg min-w-max">
                                    <div className="font-medium text-center">{member.name}</div>
                                    <div className="text-slate-300 text-[10px] text-center">{status.label}</div>
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
                                  </div>
                                </div>
                              );
                            })}
                            {activeProjectConv.team_members?.length > 5 && (
                              <Badge variant="secondary" className="text-xs">
                                +{activeProjectConv.team_members.length - 5}
                              </Badge>
                            )}
                          </div>
                          
                          {activeProjectConv.urgency_label && (
                            <Badge variant={
                              activeProjectConv.urgency_label === 'En retard' ? 'destructive' :
                              activeProjectConv.urgency_label === 'Urgent' ? 'destructive' : 'secondary'
                            } className="ml-2">
                              {activeProjectConv.urgency_label}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Warning banner */}
                        {!isClient && (
                          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                            <p className="text-xs text-amber-700">
                              <strong>Attention :</strong> Les messages sont visibles par le client
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Messages */}
                      <ScrollArea className="flex-1 p-4">
                        {projectMessages.map((msg) => (
                          <MessageBubble
                            key={msg.id}
                            message={msg}
                            isOwn={msg.sender_id === user?.id}
                            chatType="project"
                          />
                        ))}
                        <div ref={messagesEndRef} />
                      </ScrollArea>
                      
                      {/* Project closed notice */}
                      {activeProjectConv.status === 'completed' || activeProjectConv.status === 'closed' ? (
                        <div className="p-4 border-t border-slate-200 bg-slate-50">
                          <div className="flex items-center justify-center gap-3 py-4">
                            <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                            <div className="text-center">
                              <p className="font-medium text-slate-700">Projet terminé</p>
                              <p className="text-sm text-slate-500">Ce chat est maintenant en lecture seule</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                      <>
                      {/* Selected file preview */}
                      {selectedFile && (
                        <div className="px-4 py-2 bg-slate-100 border-t border-slate-300 flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-600" />
                          <span className="text-sm text-slate-700 truncate flex-1">{selectedFile.name}</span>
                          <span className="text-xs text-slate-500">
                            {(selectedFile.size / 1024).toFixed(0)} Ko
                          </span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-slate-600 hover:text-slate-800"
                            onClick={clearSelectedFile}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Audio recording preview */}
                      {showAudioOptions && audioBlob && (
                        <div className="px-4 py-3 bg-slate-100 border-t border-slate-300 flex items-center gap-3">
                          <div className="flex items-center gap-2 flex-1">
                            <Mic className="w-5 h-5 text-slate-600" />
                            <span className="text-sm text-slate-700">Message vocal enregistré</span>
                          </div>
                          <Button 
                            size="sm"
                            variant="outline"
                            onClick={sendAudioMessage}
                            disabled={sending}
                            className="border-slate-400 text-slate-700 hover:bg-slate-200"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Envoyer audio
                          </Button>
                          <Button 
                            size="sm"
                            onClick={transcribeAudio}
                            disabled={isTranscribing}
                            className="bg-slate-600 hover:bg-slate-700 text-white"
                          >
                            {isTranscribing ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <FileText className="w-4 h-4 mr-1" />
                            )}
                            Transcrire
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-slate-500 hover:text-slate-700"
                            onClick={cancelRecording}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Input with attachment, audio and deliverable buttons */}
                      <div className="p-4 border-t border-slate-200 flex items-center gap-2">
                        {/* Hidden file input */}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileSelect}
                          className="hidden"
                          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip"
                        />
                        
                        {/* Attachment button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          className="text-slate-600 hover:text-slate-800 hover:bg-slate-200"
                          title="Joindre un fichier"
                        >
                          <Paperclip className="w-5 h-5" />
                        </Button>
                        
                        {/* Audio recording button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={isRecording ? stopRecording : startRecording}
                          className={cn(
                            isRecording 
                              ? "text-red-500 hover:text-red-700 hover:bg-red-100 animate-pulse" 
                              : "text-slate-600 hover:text-slate-800 hover:bg-slate-200"
                          )}
                          title={isRecording ? "Arrêter l'enregistrement" : "Message vocal / Dictée"}
                        >
                          <Mic className="w-5 h-5" />
                        </Button>
                        
                        {/* Deliverable button (for team members) */}
                        {!isClient && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              fetchProjectTasks(activeProjectConv.id);
                              setShowDeliverableModal(true);
                            }}
                            className="text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                            title="Envoyer un livrable"
                          >
                            <Package className="w-5 h-5" />
                          </Button>
                        )}
                        
                        {/* Complete task button (for team members) */}
                        {!isClient && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              fetchProjectTasks(activeProjectConv.id);
                              setShowTaskModal(true);
                            }}
                            className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
                            title="Finaliser une tâche"
                          >
                            <CheckCircle2 className="w-5 h-5" />
                          </Button>
                        )}
                        
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder={isRecording ? "Enregistrement en cours..." : "Votre message..."}
                          className="flex-1 border-slate-300 focus:border-slate-500"
                          disabled={isRecording}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendProjectMessage();
                            }
                          }}
                        />
                        <Button 
                          onClick={handleSendProjectMessage}
                          disabled={(!newMessage.trim() && !selectedFile) || sending || isRecording}
                          size="icon"
                          className="bg-slate-600 hover:bg-slate-700"
                        >
                          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                      </div>
                      </>
                      )}
                    </>
                  ) : (
                    <EmptyState 
                      icon={FolderOpen}
                      title="Sélectionnez un projet"
                      description="Choisissez un projet pour voir ses messages"
                    />
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Direct Client Chat Tab - Agency/Team only */}
            {!isClient && (
              <TabsContent value="clients" className="flex-1 m-0 overflow-hidden">
                <div className="flex h-full">
                  {/* Clients Conversations List */}
                  <div className={cn(
                    "w-full md:w-80 border-r flex flex-col",
                    mobileShowChat && "hidden md:flex"
                  )}>
                    <div className="p-3 border-b">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input 
                          placeholder="Rechercher..."
                          className="pl-10"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                      <Button 
                        className="w-full mt-2"
                        size="sm"
                        variant="outline"
                        onClick={() => setShowNewConversation(true)}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Nouveau chat client
                      </Button>
                    </div>
                    
                    <ScrollArea className="flex-1">
                      <div className="p-2 space-y-1">
                        {/* Existing client conversations */}
                        {clientConversations
                          .filter(c => !searchQuery || 
                            c.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
                          )
                          .map(conv => (
                            <ConversationItem
                              key={conv.id}
                              conv={{
                                ...conv,
                                name: conv.client_name || conv.client?.company_name || 'Client',
                                avatar_url: conv.client?.avatar_url
                              }}
                              isActive={activeClientConv?.id === conv.id}
                              onClick={() => handleSelectClientConv(conv)}
                              type="client"
                            />
                          ))
                        }
                        
                        {clientConversations.length === 0 && !loading && (
                          <div className="text-center py-8 text-slate-500 text-sm">
                            <Building2 className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                            Aucune conversation client
                            <p className="text-xs mt-1">Cliquez sur &quot;Nouveau chat client&quot; pour commencer</p>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>

                  {/* Client Chat Area */}
                  <div className={cn(
                    "flex-1 flex flex-col",
                    !mobileShowChat && "hidden md:flex"
                  )}>
                    {activeClientConv ? (
                      <>
                        <div className="p-4 border-b flex items-center gap-3">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="md:hidden"
                            onClick={() => setMobileShowChat(false)}
                          >
                            <ArrowLeft className="w-5 h-5" />
                          </Button>
                          <Avatar 
                            src={activeClientConv.client?.avatar_url || activeClientConv.avatar_url} 
                            name={activeClientConv.client_name || activeClientConv.client?.company_name || 'Client'} 
                            size="md"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold">
                              {activeClientConv.client_name || activeClientConv.client?.company_name || 'Client'}
                            </h3>
                            <p className="text-xs text-slate-500">
                              Chat direct (hors projet)
                            </p>
                          </div>
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            <Building2 className="w-3 h-3 mr-1" />
                            Client
                          </Badge>
                        </div>
                        
                        {/* Messages */}
                        <ScrollArea className="flex-1 p-4">
                          {clientMessages.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                              <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                              <p className="font-medium">Démarrez la conversation</p>
                              <p className="text-sm mt-1">Ce chat est indépendant des projets</p>
                            </div>
                          ) : (
                            clientMessages.map((msg) => (
                              <MessageBubble
                                key={msg.id}
                                message={msg}
                                isOwn={msg.sender_id === user?.id}
                                chatType="client"
                              />
                            ))
                          )}
                          <div ref={messagesEndRef} />
                        </ScrollArea>
                        
                        {/* Selected file preview */}
                        {selectedFile && activeTab === 'clients' && (
                          <div className="px-4 py-2 bg-emerald-50 border-t border-emerald-200 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm text-emerald-700 truncate flex-1">{selectedFile.name}</span>
                            <span className="text-xs text-emerald-500">
                              {(selectedFile.size / 1024).toFixed(0)} Ko
                            </span>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6 text-emerald-600 hover:text-emerald-800"
                              onClick={clearSelectedFile}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        
                        {/* Audio recording preview */}
                        {showAudioOptions && audioBlob && activeTab === 'clients' && (
                          <div className="px-4 py-3 bg-emerald-50 border-t border-emerald-200 flex items-center gap-3">
                            <div className="flex items-center gap-2 flex-1">
                              <Mic className="w-5 h-5 text-emerald-600" />
                              <span className="text-sm text-emerald-700">Message vocal enregistré</span>
                            </div>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={handleSendClientMessage}
                              disabled={sending}
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Envoyer audio
                            </Button>
                            <Button 
                              size="sm"
                              onClick={transcribeAudio}
                              disabled={isTranscribing}
                              className="bg-emerald-500 hover:bg-emerald-600 text-white"
                            >
                              {isTranscribing ? (
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4 mr-1" />
                              )}
                              Transcrire
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-slate-500 hover:text-slate-700"
                              onClick={cancelRecording}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        
                        {/* Input with emerald/green theme */}
                        <div className="p-4 border-t border-emerald-100 flex items-center gap-2">
                          {/* File input */}
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                            className="hidden"
                            accept="*/*"
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => fileInputRef.current?.click()}
                            className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
                            title="Joindre un fichier"
                          >
                            <Paperclip className="w-5 h-5" />
                          </Button>
                          
                          {/* Audio recording button */}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={isRecording ? stopRecording : startRecording}
                            className={cn(
                              isRecording 
                                ? "text-red-500 hover:text-red-700 hover:bg-red-100 animate-pulse" 
                                : "text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
                            )}
                            title={isRecording ? "Arrêter l'enregistrement" : "Message vocal / Dictée"}
                          >
                            <Mic className="w-5 h-5" />
                          </Button>
                          
                          <Input
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder={isRecording ? "Enregistrement en cours..." : "Votre message au client..."}
                            className="flex-1 border-emerald-200 focus:border-emerald-400"
                            disabled={isRecording}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendClientMessage();
                              }
                            }}
                          />
                          <Button 
                            onClick={handleSendClientMessage}
                            disabled={(!newMessage.trim() && !selectedFile && !audioBlob) || sending || isRecording}
                            size="icon"
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <EmptyState 
                        icon={Building2}
                        title="Contactez un client"
                        description="Sélectionnez un client pour démarrer une conversation directe"
                      />
                    )}
                  </div>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </Card>
      </div>

      {/* New Conversation Dialog */}
      <Dialog open={showNewConversation} onOpenChange={(open) => {
        setShowNewConversation(open);
        if (!open) setSelectedNewConvRecipient('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle conversation</DialogTitle>
            <DialogDescription>
              {activeTab === 'clients' 
                ? 'Sélectionnez un client à contacter'
                : 'Sélectionnez les membres pour démarrer une conversation'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label className="mb-2 block">
              {activeTab === 'clients' ? 'Client' : 'Membre(s)'}
            </Label>
            <Select value={selectedNewConvRecipient} onValueChange={setSelectedNewConvRecipient}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {activeTab === 'clients' 
                  ? clients.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.company_name || `${c.first_name} ${c.last_name}`}
                      </SelectItem>
                    ))
                  : teamMembers.filter(m => m.id !== user?.id).map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.first_name} {m.last_name}
                      </SelectItem>
                    ))
                }
              </SelectContent>
            </Select>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewConversation(false)}>
              Annuler
            </Button>
            <Button 
              style={{ backgroundColor: theme.colors?.accent }}
              disabled={!selectedNewConvRecipient}
              onClick={async () => {
                if (activeTab === 'clients') {
                  const client = clients.find(c => c.id === selectedNewConvRecipient);
                  if (client) {
                    await handleStartClientConversation(client);
                    setShowNewConversation(false);
                    setSelectedNewConvRecipient('');
                  }
                } else {
                  // Internal conversation
                  try {
                    const res = await api.post('/internal-chat/conversations', {
                      participant_ids: [selectedNewConvRecipient],
                      name: null
                    });
                    const conv = res.data;
                    setInternalConversations(prev => [conv, ...prev.filter(c => c.id !== conv.id)]);
                    handleSelectInternalConv(conv);
                    toast.success('Conversation créée');
                    setShowNewConversation(false);
                    setSelectedNewConvRecipient('');
                  } catch (error) {
                    toast.error('Erreur lors de la création');
                  }
                }
              }}
            >
              Démarrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deliverable Modal - For team members to send deliverables */}
      <Dialog open={showDeliverableModal} onOpenChange={setShowDeliverableModal}>
        <DialogContent className="max-w-lg w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-500" />
              Envoyer un livrable
            </DialogTitle>
            <DialogDescription>
              Le client pourra valider ou demander des modifications
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label htmlFor="deliverable-title">Titre du livrable *</Label>
              <Input
                id="deliverable-title"
                value={deliverableTitle}
                onChange={(e) => setDeliverableTitle(e.target.value)}
                placeholder="Ex: Maquette page d'accueil v2"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="deliverable-task">Tâche associée (optionnel)</Label>
              <Select value={selectedTaskForDeliverable} onValueChange={setSelectedTaskForDeliverable}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner une tâche..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune tâche</SelectItem>
                  {projectTasks.map((task) => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.title || task.name || 'Tâche sans nom'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* New version option */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
              <div>
                <Label className="cursor-pointer">Nouvelle version d'un livrable existant</Label>
                <p className="text-xs text-slate-500 mt-1">Cochez si c'est une révision d'un livrable précédent</p>
              </div>
              <Switch 
                checked={isNewVersion} 
                onCheckedChange={(checked) => {
                  setIsNewVersion(checked);
                  if (!checked) setPreviousDeliverableId('');
                }}
              />
            </div>
            
            {/* Previous deliverable selector */}
            {isNewVersion && projectDeliverables.length > 0 && (
              <div>
                <Label>Livrable précédent</Label>
                <Select value={previousDeliverableId} onValueChange={setPreviousDeliverableId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Sélectionner le livrable à mettre à jour..." />
                  </SelectTrigger>
                  <SelectContent>
                    {projectDeliverables.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name} (v{d.version || 1}) - {d.status === 'pending' ? 'En attente' : d.status === 'approved' ? 'Validé' : d.status === 'revision_requested' ? 'Modif. demandées' : d.status}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">La version sera incrémentée automatiquement</p>
              </div>
            )}

            {/* Type de livrable */}
            <div>
              <Label>Type de livrable *</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => { setDeliverableType('link_web'); setDeliverableFile(null); }}
                  className={cn(
                    "p-3 rounded-lg border-2 text-center transition-all",
                    deliverableType === 'link_web' 
                      ? "border-blue-500 bg-blue-50 text-blue-700" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <Globe className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">Lien web</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setDeliverableType('link_video'); setDeliverableFile(null); }}
                  className={cn(
                    "p-3 rounded-lg border-2 text-center transition-all",
                    deliverableType === 'link_video' 
                      ? "border-purple-500 bg-purple-50 text-purple-700" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <Video className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">Vidéo</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setDeliverableType('image'); setDeliverableLink(''); }}
                  className={cn(
                    "p-3 rounded-lg border-2 text-center transition-all",
                    deliverableType === 'image' 
                      ? "border-amber-500 bg-amber-50 text-amber-700" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <ImageIcon className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">Image</span>
                </button>
                <button
                  type="button"
                  onClick={() => { setDeliverableType('pdf'); setDeliverableLink(''); }}
                  className={cn(
                    "p-3 rounded-lg border-2 text-center transition-all",
                    deliverableType === 'pdf' 
                      ? "border-red-500 bg-red-50 text-red-700" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                >
                  <FileText className="w-5 h-5 mx-auto mb-1" />
                  <span className="text-xs font-medium">PDF</span>
                </button>
              </div>
            </div>

            {/* Guide selon le type */}
            {deliverableType === 'link_web' && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span>Collez l'URL du site web à valider (ex: https://mon-site.fr)</span>
                </p>
              </div>
            )}
            {deliverableType === 'link_video' && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <p className="text-xs text-purple-700 flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  <span>Collez le lien YouTube, Vimeo ou autre plateforme vidéo</span>
                </p>
              </div>
            )}
            {deliverableType === 'image' && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-xs text-amber-700 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" />
                  <span>Formats acceptés : PNG, JPEG, WEBP</span>
                </p>
              </div>
            )}
            {deliverableType === 'pdf' && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-xs text-red-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  <span>Idéal pour les maquettes, présentations et documents</span>
                </p>
              </div>
            )}
            
            <div>
              <Label htmlFor="deliverable-comment">Description / Commentaire</Label>
              <Textarea
                id="deliverable-comment"
                value={deliverableComment}
                onChange={(e) => setDeliverableComment(e.target.value)}
                placeholder="Détails sur ce livrable..."
                className="mt-1"
                rows={3}
              />
            </div>

            {/* Lien (pour link_web et link_video) */}
            {(deliverableType === 'link_web' || deliverableType === 'link_video') && (
              <div>
                <Label htmlFor="deliverable-link">
                  {deliverableType === 'link_web' ? 'URL du site web *' : 'URL de la vidéo *'}
                </Label>
                <Input
                  id="deliverable-link"
                  type="url"
                  value={deliverableLink}
                  onChange={(e) => setDeliverableLink(e.target.value)}
                  placeholder={deliverableType === 'link_web' ? 'https://www.exemple.fr' : 'https://youtube.com/watch?v=...'}
                  className="mt-1"
                />
              </div>
            )}
            
            {/* Fichier (pour image et pdf) */}
            {(deliverableType === 'image' || deliverableType === 'pdf') && (
              <div>
                <Label>Fichier à valider *</Label>
                <div className="mt-1">
                  <input
                    type="file"
                    ref={deliverableInputRef}
                    onChange={(e) => setDeliverableFile(e.target.files?.[0] || null)}
                    className="hidden"
                    accept={deliverableType === 'image' ? '.png,.jpg,.jpeg,.webp' : '.pdf'}
                  />
                  {deliverableFile ? (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      {deliverableType === 'image' ? (
                        <ImageIcon className="w-5 h-5 text-amber-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-red-600" />
                      )}
                      <span className="flex-1 truncate text-sm">{deliverableFile.name}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => setDeliverableFile(null)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full border-dashed border-amber-400 text-amber-700 hover:bg-amber-50"
                      onClick={() => deliverableInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {deliverableType === 'image' ? 'Joindre une image' : 'Joindre un PDF'}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeliverableModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendDeliverable}
              disabled={
                !deliverableTitle.trim() || 
                sendingDeliverable ||
                ((deliverableType === 'link_web' || deliverableType === 'link_video') && !deliverableLink.trim()) ||
                ((deliverableType === 'image' || deliverableType === 'pdf') && !deliverableFile)
              }
              className="bg-amber-500 hover:bg-amber-600"
            >
              {sendingDeliverable ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Package className="w-4 h-4 mr-2" />
              )}
              Envoyer le livrable
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal - For viewing deliverables in full size */}
      <Dialog open={!!previewImage} onOpenChange={() => setPreviewImage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b bg-slate-50">
            <DialogTitle className="flex items-center gap-2">
              {previewImage?.deliverable ? (
                <>
                  <Package className="w-5 h-5 text-amber-500" />
                  Livrable v{previewImage.deliverable.version || 1}
                  {previewImage.deliverable.status === 'pending' && (
                    <Badge variant="outline" className="ml-2 border-amber-400 text-amber-700">En attente de validation</Badge>
                  )}
                  {previewImage.deliverable.status === 'approved' && (
                    <Badge className="ml-2 bg-emerald-500 text-white">Validé ✓</Badge>
                  )}
                  {previewImage.deliverable.status === 'revision_requested' && (
                    <Badge className="ml-2 bg-orange-500 text-white">Modifications demandées</Badge>
                  )}
                </>
              ) : (
                <>
                  <ImageIcon className="w-5 h-5 text-blue-500" />
                  Aperçu de l&apos;image
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex items-center justify-center bg-slate-900 p-4 min-h-[300px] max-h-[60vh] overflow-auto">
            {previewImage && (
              <img 
                src={previewImage.url} 
                alt={previewImage.name || 'Image'} 
                className="max-w-full max-h-full object-contain rounded"
              />
            )}
          </div>
          
          <DialogFooter className="p-4 border-t bg-slate-50 flex-col sm:flex-row gap-2">
            <div className="flex-1 text-sm text-slate-500 truncate">
              {previewImage?.name}
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => window.open(previewImage?.url, '_blank')}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
              
              {/* Validation buttons for pending deliverables - client only */}
              {isClient && previewImage?.deliverable && previewImage.deliverable.status === 'pending' && (
                <>
                  <Button
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    onClick={() => {
                      handleDeliverableAction(previewImage.message.deliverable_id, 'approve');
                      setPreviewImage(null);
                    }}
                  >
                    <ThumbsUp className="w-4 h-4 mr-2" />
                    Valider
                  </Button>
                  <Button
                    variant="outline"
                    className="border-orange-400 text-orange-700 hover:bg-orange-50"
                    onClick={() => {
                      setFeedbackDeliverableId(previewImage.message.deliverable_id);
                      setShowFeedbackModal(true);
                      setPreviewImage(null);
                    }}
                  >
                    <ThumbsDown className="w-4 h-4 mr-2" />
                    Modifications
                  </Button>
                </>
              )}
              
              <Button variant="ghost" onClick={() => setPreviewImage(null)}>
                Fermer
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feedback Modal - For client to request modifications */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-orange-500" />
              Demander des modifications
            </DialogTitle>
            <DialogDescription>
              Décrivez les modifications souhaitées
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="feedback-text">Vos remarques</Label>
            <Textarea
              id="feedback-text"
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder="Décrivez les modifications que vous souhaitez..."
              className="mt-1"
              rows={4}
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={() => handleDeliverableAction(feedbackDeliverableId, 'reject', feedbackText)}
              disabled={!feedbackText.trim()}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Envoyer la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Completion Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              Finaliser une tâche
            </DialogTitle>
            <DialogDescription>
              Sélectionnez la tâche que vous avez terminée
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-3 max-h-[300px] overflow-y-auto">
            {projectTasks.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <ListTodo className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <p className="text-sm">Aucune tâche en cours</p>
              </div>
            ) : (
              projectTasks.map((task) => (
                <div 
                  key={task.id}
                  className="p-3 border rounded-lg hover:bg-emerald-50 hover:border-emerald-200 transition-colors cursor-pointer group"
                  onClick={() => handleCompleteTask(task.id)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 group-hover:bg-emerald-200 transition-colors">
                      <Check className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate">{task.title || task.name || 'Tâche sans nom'}</p>
                      <p className="text-xs text-slate-500">
                        {task.status === 'in_progress' ? '🔄 En cours' : 
                         task.status === 'pending' ? '⏳ En attente' : '📝 À faire'}
                      </p>
                    </div>
                    <CheckCircle2 className="w-5 h-5 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
              ))
            )}
          </div>
          
          {completingTask && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              <span className="ml-2 text-sm text-slate-600">Mise à jour...</span>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskModal(false)}>
              Annuler
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </MainLayout>
      </div>
    </>
  );
};

export default UnifiedChatPage;
